import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { Instagram, Facebook } from "lucide-react";

const LINKS = [
    { id: "work", label: "Portfolio", num: "01" },
    { id: "services", label: "Servizi", num: "02" },
    { id: "about", label: "Chi sono", num: "03" },
    { id: "contact", label: "Contatti", num: "04" },
];

const SOCIALS = [
    { href: "https://www.instagram.com/francescociotoliphoto/", label: "Instagram", Icon: Instagram },
    { href: "https://www.facebook.com/francescociotoli.photo", label: "Facebook", Icon: Facebook },
];

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    const go = (id) => {
        setOpen(false);
        setTimeout(() => {
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 250);
    };

    return (
        <>
            <motion.header
                initial={{ y: -60, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1], delay: 1.4 }}
                className={`fixed top-[32px] sm:top-[36px] md:top-[40px] left-0 right-0 z-50 transition-colors duration-500 ${scrolled || open ? "bg-ink-950/90 backdrop-blur-xl border-b border-white/5" : ""}`}
                data-testid="nav-bar"
            >
                <div className="flex items-center justify-between px-5 md:px-10 py-4 md:py-5">
                    <button
                        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                        className="font-mono text-[10px] md:text-[11px] tracking-[0.25em] uppercase text-bone-50"
                        data-testid="nav-brand"
                    >
                        FC<span className="text-flash">.</span>studio
                    </button>

                    {/* Desktop */}
                    <nav className="hidden md:flex items-center gap-10">
                        {LINKS.map((l) => (
                            <button
                                key={l.id}
                                onClick={() => go(l.id)}
                                className="font-mono text-[11px] tracking-[0.25em] uppercase text-bone-50 link-underline"
                                data-testid={`nav-link-${l.id}`}
                            >
                                {l.label}
                            </button>
                        ))}
                    </nav>
                    <div
                        className="hidden md:inline-flex items-center gap-3 text-bone-50"
                        data-testid="nav-socials"
                    >
                        {SOCIALS.map(({ href, label, Icon }) => (
                            <a
                                key={label}
                                href={href}
                                target="_blank"
                                rel="noreferrer"
                                aria-label={label}
                                className="p-2 border border-white/15 hover:border-flash hover:text-flash text-bone-50 transition-colors duration-300 rounded-full"
                                data-testid={`nav-social-${label.toLowerCase()}`}
                            >
                                <Icon size={15} strokeWidth={1.5} />
                            </a>
                        ))}
                    </div>

                    {/* Mobile burger */}
                    <button
                        onClick={() => setOpen((o) => !o)}
                        aria-label={open ? "Chiudi menu" : "Apri menu"}
                        aria-expanded={open}
                        className="md:hidden relative w-10 h-10 flex flex-col items-center justify-center gap-[6px] z-[60]"
                        data-testid="nav-burger"
                    >
                        <motion.span
                            animate={open ? { rotate: 45, y: 4 } : { rotate: 0, y: 0 }}
                            transition={{ duration: 0.4, ease: [0.77, 0, 0.18, 1] }}
                            className="block w-6 h-[1.5px] bg-bone-50 origin-center"
                        />
                        <motion.span
                            animate={open ? { rotate: -45, y: -4 } : { rotate: 0, y: 0 }}
                            transition={{ duration: 0.4, ease: [0.77, 0, 0.18, 1] }}
                            className="block w-6 h-[1.5px] bg-bone-50 origin-center"
                        />
                    </button>
                </div>
            </motion.header>

            {/* Mobile overlay menu */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ y: "-100%" }}
                        animate={{ y: 0 }}
                        exit={{ y: "-100%" }}
                        transition={{ duration: 0.6, ease: [0.77, 0, 0.18, 1] }}
                        className="fixed inset-0 top-[32px] sm:top-[36px] md:top-[40px] z-[55] bg-ink-950 md:hidden flex flex-col"
                        data-testid="mobile-menu"
                    >
                        <div className="pt-24 px-6 flex-1 flex flex-col justify-center">
                            <div className="overline mb-8">Menu</div>
                            <ul className="space-y-6">
                                {LINKS.map((l, i) => (
                                    <motion.li
                                        key={l.id}
                                        initial={{ opacity: 0, y: 30 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6, delay: 0.2 + i * 0.08, ease: [0.19, 1, 0.22, 1] }}
                                    >
                                        <button
                                            onClick={() => go(l.id)}
                                            className="flex items-baseline gap-4 text-left w-full"
                                            data-testid={`mobile-nav-link-${l.id}`}
                                        >
                                            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-bone-600">{l.num}</span>
                                            <span className="font-display uppercase text-bone-50 leading-none tracking-[-0.02em]" style={{ fontSize: "clamp(2.5rem, 12vw, 5rem)" }}>
                                                {l.label}
                                                <span className="text-flash">.</span>
                                            </span>
                                        </button>
                                    </motion.li>
                                ))}
                            </ul>
                        </div>
                        <div className="px-6 pb-10 border-t border-white/10 pt-6 flex items-center justify-between">
                            <div className="flex items-center gap-3" data-testid="mobile-socials">
                                {SOCIALS.map(({ href, label, Icon }) => (
                                    <a
                                        key={label}
                                        href={href}
                                        target="_blank"
                                        rel="noreferrer"
                                        aria-label={label}
                                        onClick={() => setOpen(false)}
                                        className="p-2.5 border border-white/15 rounded-full text-bone-50 hover:border-flash hover:text-flash transition-colors"
                                        data-testid={`mobile-social-${label.toLowerCase()}`}
                                    >
                                        <Icon size={18} strokeWidth={1.5} />
                                    </a>
                                ))}
                            </div>
                            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-bone-400">Ceccano · FR</span>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
